// src/components/booking/BookingList.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Ship, Package, AlertCircle, CheckCircle, Sparkles,Activity,
  ArrowRight, ArrowUpRight, Truck, List, LayoutGrid, Plus, BookmarkPlus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getBookingList, deletebookingDetails,
  getBookingDetails, deleteBookingList,
} from "@/redux/Booking/actions";
import { callLoader } from "@/redux/Loader/actions";
import { toast } from "sonner";
import Modal from "../ui/Modal";
import FileUpload from "../FileUpload/FileUpload";
import UploadNotes from "../NotesUpload/NotesUpload";
import Swal from "sweetalert2";
import FilterBar from "../shipment/FilterBar";
import getColumns from "./bookingColumn";
import ExpandedDetails from "./ExpandedDetails";
import ShipmentCard from "../shipment/ShipmentCard";
import Pagination from "@/pages/PortPlace/Pagination";
import { useDebounced } from "@/hooks/useDebounced";
import BookingCancelModal from "../Modal/BookingCancelModal";
import { showToast } from "@/redux/Toast/actions";
import AIInsightCard from "../shipment/AIInsightCard";
import AIInsightsModal from "../shipment/AIInsightsModal";

// ─── Animated typewriter title ────────────────────────────────────────────────
const TITLE = "Export Shipment List";
function AnimatedTitle() {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                 text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: "0ch" }}
        animate={{ width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"] }}
        transition={{
          duration: 4.8,
          times: [0, 0.22, 0.9, 1],
          ease: ["linear", "linear", "easeInOut"],
          repeat: Infinity,
        }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

// ─── View Toggle ──────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
  const base =
    "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
  const active =
    "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
  const inactive =
    "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";

  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                 bg-white dark:bg-gray-900 p-0.5"
    >
      <button
        onClick={() => setView("table")}
        title="List view"
        className={`${base} ${view === "table" ? active : inactive}`}
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => setView("card")}
        title="Grid view"
        className={`${base} ${view === "card" ? active : inactive}`}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const BookingList = ({ onCreateNew, onViewShipment, onPrintShipment, onOpenMail }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { bookingList, pagination, summary } = useSelector((s) => s.booking);
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const authName = authUser?.username;

  const [showNote, setshowNote] = useState(false);
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [charges, setCharges] = useState([]);
  const [payload, setPayload] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [expand, setExpand] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [view, setView] = useState("table");
  const [left, setLeft] = useState(0);
  const [deleteData, setDeleteData] = useState(null);

  const divRef = useRef(null);
  const [width, setWidth] = useState(0);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchBookings = async (page = currentPage, filter = null) => {
    dispatch(callLoader(1));
    setIsLoading(true);
    try {
      await dispatch(
        getBookingList({
          IPageNo: page,
          IPageRecords: filter?.pageSize || recordsPerPage,
          FilterType: filter?.status || "ALL",
          SearchKeyword: filter?.search || "",
          CARRIER: filter?.carrier || "",
          pol: filter?.pol || "",
          pod: filter?.pod || "",
          shortby: filter?.sortBy || "",
          FromDate: filter?.dateFrom || "",
          ToDate: filter?.dateTo || "",
          compnayId: authUser?.companyId,
        })
      );
      setCurrentPage(page);
    } catch {
      toast.error(t("failedLoadShipments"));
    } finally {
      dispatch(callLoader(0));
      setIsLoading(false);
    }
  };
  

  useEffect(() => { fetchBookings(1); }, []);

  useEffect(() => {
    const update = () => divRef.current && setWidth(divRef.current.offsetWidth);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [divRef, bookingList]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFilter = (f) => { setPayload(f); fetchBookings(1, f); };
  const handlePageSize = (size) => { setRecordsPerPage(size); fetchBookings(1, { pageSize: size }); setCurrentPage(1); };
const uniqueCarriers = [
  ...new Map(
    bookingList
      .filter(s => s.CarrierSCAC && s.CarrierName)
      .map(s => [
        `${s.CarrierSCAC}|${s.CarrierName}`,
        { CarrierSCAC: s.CarrierSCAC, CarrierName: s.CarrierName }
      ])
  ).values()
];

  const handlePrint = (shipment) => window.open(`/booking/print/${btoa(shipment?.id)}`, "_blank");
  const changePage = (page) => { if (currentPage === page) return; setCurrentPage(page); fetchBookings(page, payload); };

  const openBookingInNewTab = useCallback((shipmentOrId) => {
    const id =
      typeof shipmentOrId === "object"
        ? shipmentOrId?.id ?? shipmentOrId?.BookingId
        : shipmentOrId;
    if (!id) return;
    window.open(
      `${window.location.origin}/booking/edit/${id}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, []);

  const openNewBookingInNewTab = useCallback(() => {
    window.open(
      `${window.location.origin}/booking/new`,
      "_blank",
      "noopener,noreferrer",
    );
  }, []);

  const handleViewShipment = onViewShipment ?? openBookingInNewTab;

  const handleDeleteBooking = async (id) => {
    const result = await Swal.fire({
      title: t("areYouSure"),
      text: "This booking will be inactivated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    });
    if (!result.isConfirmed) return;
    try {
      await deletebookingDetails({ BookingId: id, ModifiedBy: `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim() });
      await fetchBookings(1, payload);
      toast.success(t("bookingInactivatedSuccessfully"));
    } catch (e) { toast.error(e); }
  };

  const handleOpenNotes = (s) => { setSelectedOrderId(s); setshowNote(true); };
  const handleOpenFileUpload = (s) => { setSelectedOrderId(s); setFileUploadVisible(true); };
  const handleEmail = (s) => navigate(`/mail/compose?id=${s?.id || s?.BookingId || s?.BolID}&type=Booking`);
  const _setRestoreDataPopup = (data) => setDeleteData(data);
  const _setRestoreData = async (remark) => {
    await deleteBookingList({ BookingId: deleteData?.id || 0, ModifyBy: authName, BookingCancelRemarks: remark })
      .then(() => fetchBookings(currentPage))
      .catch((e) => dispatch(showToast("error", e?.message || t("failedRestoreShipment"))))
      .finally(() => setDeleteData(null));
  };

  async function _getBooking(id) {
    try {
      const result = await dispatch(getBookingDetails(id,authUser?.companyId));
      setCharges(result?.rates ?? []);
    } catch { toast.error(t("failedLoadBookingDetails")); }
  }

  const toggleExpandRow = (id) => {
    if (expandedRow === id) { setExpandedRow(null); }
    else { setExpandedRow(id); _getBooking(id); }
  };

  const debouncedScroll = useDebounced((sl) => setLeft(sl), 100);
  const onScroll = (e) => debouncedScroll(e.target.scrollLeft);

  const ExpandedRow = useCallback(
    (shipment) => (
      <AnimatePresence initial={false}>
        {expandedRow === shipment.id && (
          <tr>
            <motion.td
              colSpan={24} className="p-0"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ExpandedDetails
                shipment={shipment}
                setExpandedRow={setExpandedRow}
                charges={charges}
                setFileUploadVisible={setFileUploadVisible}
                setSelectedOrderId={setSelectedOrderId}
                onOpenMail={handleEmail}
                width={width} left={left}
                onPrintShipment={() => window.open(`/booking/print/${btoa(shipment.id)}`, "_blank")}
              />
            </motion.td>
          </tr>
        )}
      </AnimatePresence>
    ),
    [expandedRow, width, left]
  );

  // ── KPI cards data ────────────────────────────────────────────────────────
  const insightCards = useMemo(() => [
    { icon: Package,      title: t("totalShipments"), value: summary?.total          ?? 0, trend: "+12%",              trendUp: true,  accentKey: "blue"    },
    { icon: Activity,  title: t("active"),         value: summary?.active         ?? 0, trend: t("onTrack"),        trendUp: true,  accentKey: "emerald" },
    { icon: Truck,        title: t("inTransit"),      value: summary?.intransit      ?? 0, trend: "3 arriving",        trendUp: true,  accentKey: "amber"   },
    { icon: AlertCircle,  title: t("needsAttention"), value: summary?.needattension  ?? 0, trend: t("reviewRequired"), trendUp: false, accentKey: "red"     },
  ], [summary, t])
  console.log("Summary:", summary, "Insight Cards:", insightCards);

  // ── Shared card props ─────────────────────────────────────────────────────
  const cardProps = (shipment, index) => ({
    key: shipment.id || index,
    shipment,
    onView: handleViewShipment,
    onPrint: handlePrint,
    onEmail: handleEmail,
   onDelete: () => handleDeleteBooking(shipment?.id || shipment?.BookingId),
    onOpenNotes: handleOpenNotes,
    onOpenFileUpload: handleOpenFileUpload,
    expand, setExpand,
    
  });

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full space-y-3">

      {/* ════════════════════════════════════════════════════
          HEADER CARD
      ════════════════════════════════════════════════════ */}
      <div className="rounded-2xl overflow-hidden
                      border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900
                      shadow-sm dark:shadow-gray-900/40">

        {/* ── Top bar ─────────────────────────────────────── */}
        <div className="px-4 sm:px-5 py-4
                        flex flex-wrap items-center justify-between gap-3
                        border-b border-gray-100 dark:border-gray-800">

          {/* Left — icon + title + desc */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                            bg-blue-50 dark:bg-blue-900/40
                            ring-1 ring-blue-100 dark:ring-blue-800
                            flex items-center justify-center">
              <Ship className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400
                            hidden sm:block truncate max-w-sm lg:max-w-lg">
                Track, manage, and optimize all your shipments with AI-powered insights.
              </p>
            </div>
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Rate Quote */}
            <button
              onClick={() => navigate("/rate-quote")}
              className="inline-flex items-center gap-2 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-white dark:bg-gray-800
                         border border-gray-200 dark:border-gray-700
                         text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 hover:border-gray-300
                         dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                         text-[12.5px] font-semibold transition-colors"
            >
              <BookmarkPlus className=" w-3.5 h-3.5 " />
              <span className="flex items-center">Export Rate Quote</span>
             
            </button>

            {/* New Booking */}
            <button
              onClick={openNewBookingInNewTab}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                         dark:bg-blue-500 dark:hover:bg-blue-600
                         text-white text-[12.5px] font-semibold
                         shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                         dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                         transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {/* <span className="hidden xs:inline">New Booking</span> */}
              <span >New Shipment </span>
            </button>

            {/* View toggle */}
            <ViewToggle view={view} setView={setView} />
          </div>
        </div>

        {/* ── KPI Cards ───────────────────────────────────── */}
        <div className="px-4 sm:px-5 pt-4 pb-3
                        grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
          {insightCards.map((card) => (
            <AIInsightCard key={card.title} {...card} />
          ))}
        </div>

        {/* ── AI Insight Banner ────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mx-4 sm:mx-5 mb-4 relative overflow-hidden
                     flex flex-col sm:flex-row items-start sm:items-center gap-3
                     rounded-xl px-4 py-3
                     bg-gradient-to-r from-violet-50 to-indigo-50
                     dark:from-violet-950/50 dark:to-indigo-950/50
                     border border-violet-100 dark:border-violet-900/60"
        >
          {/* Left accent stripe */}
          <span
            aria-hidden
            className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl
                       bg-gradient-to-b from-violet-500 via-fuchsia-500 to-pink-500"
          />

          {/* Sparkle icon */}
          <div className="shrink-0 w-9 h-9 rounded-lg ml-1
                          bg-gradient-to-br from-violet-500 to-indigo-500
                          flex items-center justify-center
                          shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
            <Sparkles className="w-4 h-4 text-white" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em]
                            text-violet-600 dark:text-violet-400">
              AI Insight
            </div>
            <p className="mt-0.5 text-[12.5px] leading-relaxed
                          text-gray-700 dark:text-gray-300">
              Based on current trends,{" "}
              <span className="font-semibold text-gray-900 dark:text-white">85%</span>{" "}
              of shipments are expected to arrive on time. Consider prioritizing{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                2 delayed shipments
              </span>{" "}
              from MAERSK carrier.
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => setShowAIInsights(true)}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                       text-[12px] font-semibold
                       text-violet-700 dark:text-violet-300
                       bg-violet-100 dark:bg-violet-900/50
                       hover:bg-violet-200 dark:hover:bg-violet-900
                       ring-1 ring-violet-200 dark:ring-violet-800
                       transition-colors"
            data-testid="view-ai-insights-btn"
          >
            View Details <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      </div>
      {/* ════ END HEADER CARD ════ */}

      {/* ── Filter Bar ──────────────────────────────────────── */}
      <FilterBar
        uniqueCarriers={uniqueCarriers}
        onFilter={handleFilter}
        stats={{
          total: pagination?.totalRecord,
          showRecord: pagination?.currentRecord || 0,
        }}
      />

      {/* ── Content ─────────────────────────────────────────── */}
      <div >
        {bookingList.length === 0 ? (
          /* Empty state */
          <div className="text-center py-20
                          bg-white dark:bg-gray-900
                          rounded-2xl border border-gray-200 dark:border-gray-800">
            <Ship className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">
              No shipments found
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
              Create a new booking to get started
            </p>
            <button
              onClick={openNewBookingInNewTab}
              className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl
                         text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              + Create Booking
            </button>
          </div>
        ) : view !== "table" ? (
          /* Card grid */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bookingList.map((shipment, i) => (
              <ShipmentCard {...cardProps(shipment, i)} />
            ))}
          </div>
        ) : (
          /* Table / list view */
          <div className="space-y-4 overflow-auto rounded-xl mt-4" ref={divRef} onScroll={onScroll}>
            {bookingList.map((shipment, i) => (
              <ShipmentCard {...cardProps(shipment, i)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && pagination?.totalPage > 0 && (
          <Pagination
            className="border-t border-gray-200 dark:border-gray-800
                       pl-2 pr-16 pt-4 pb-8 mb-4 mt-8
                       bg-gray-50 dark:bg-gray-800/50"
            currentPage={currentPage}
            recordsPerPage={recordsPerPage}
            setRecordsPerPage={handlePageSize}
            onPrevious={changePage}
            onNext={changePage}
            totalRecord={pagination?.totalRecord}
            handleGoto={changePage}
            title="shipments"
          />
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────── */}
      <AnimatePresence>
        {showAIInsights && (
          <AIInsightsModal
            isOpen={showAIInsights}
            onClose={() => setShowAIInsights(false)}
            shipments={bookingList}
            insightCards={insightCards}
          />
        )}
      </AnimatePresence>

      <Modal isOpen={fileUploadVisible} onClose={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}>
        <FileUpload orderId={selectedOrderId?.id} orderNum={selectedOrderId?.fileNumber} type="booking" />
      </Modal>

      <Modal isOpen={showNote} onClose={() => { setshowNote(false); setSelectedOrderId(null); }}>
        <UploadNotes BookingID={selectedOrderId?.id || ""} RefNumber={selectedOrderId?.fileNumber} type="Booking" />
      </Modal>

      {deleteData && (
        <BookingCancelModal
          open
          onClose={() => setDeleteData(null)}
          onDelete={_setRestoreData}
        />
      )}
    </div>
  );
};

export default BookingList;